import { defineStore } from 'pinia';
import {
  computed,
  ref,
  useCookie,
  useFetch,
  useRuntimeConfig,
  navigateTo,
  nextTick,
  type Ref,
} from '#imports';
import {
  asAuthUser,
  authUserHeaders,
  runLogoutFlow,
  usePermissionChecker,
  hasRole,
  hasMatch,
  getAuthCookieOptions,
} from 'r3ui-lib';
import type { AuthUserDto } from '~/types/ports/auth.api';
import {
  isUnauthorizedError,
  presentPageLoadError,
} from '~/utils/presentPageLoadError';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const config = useRuntimeConfig();
    const { authApiUrl, origin } = config.public;
    // Опции обязаны совпадать с теми, с которыми cookie ставит серверный
    // callback: без domain удаление не снимет cookie родительского домена.
    const cookie = useCookie<string | null>(
      'auth_token',
      getAuthCookieOptions({
        origin: origin as string | undefined,
        authCookieDomain: config.public.authCookieDomain as string | undefined,
      })
    ) as Ref<string | null>;
    const token = ref<string | null | undefined>(cookie.value);
    const user = ref<AuthUserDto | null>(null);
    let fetchVersion = 0;

    const isAuthenticated = computed(() => Boolean(token.value));

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map((role: { name?: string }) => role?.name)
        .filter((roleName: string | undefined): roleName is string =>
          Boolean(roleName)
        );
    });

    const hasUserRole = (role: string | string[]) => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }

      return hasRole(userRoleNames.value, role);
    };

    async function fetchUser() {
      if (!token.value) {
        return false;
      }
      const requestedToken = token.value;
      const version = ++fetchVersion;
      const isCurrent = () =>
        version === fetchVersion && token.value === requestedToken;
      try {
        const { data, error } = await useFetch<AuthUserDto>(
          '/api/services/auth/user',
          {
            headers: authUserHeaders(String(requestedToken)),
          }
        );
        if (!isCurrent()) return false;
        if (error.value) {
          if (isUnauthorizedError(error.value)) {
            resetAll();
            return false;
          }
          presentPageLoadError(error.value);
          return false;
        }
        const loaded = asAuthUser<AuthUserDto>(data.value);
        if (!loaded) {
          presentPageLoadError(new Error('fetchUser: пустой ответ'));
          return false;
        }
        user.value = loaded;
        return true;
      } catch (error) {
        if (!isCurrent()) return false;
        if (isUnauthorizedError(error)) {
          resetAll();
          return false;
        }
        presentPageLoadError(error);
        return false;
      }
    }

    function resetStorage() {
      ['breadcrumbs', 'filters', 'tables'].forEach(key => {
        localStorage.removeItem(key);
      });
    }

    async function logOutSSO() {
      return $fetch('/api/auth/logout', { method: 'POST' });
    }

    function resetAll() {
      fetchVersion++;
      token.value = null;
      user.value = null;
      cookie.value = null;
    }

    async function logout() {
      fetchVersion++;
      const baseLogoutUrl = authApiUrl ? `${authApiUrl}/logout` : '/login';
      const redirectPath = origin
        ? `${baseLogoutUrl}?redirect=${encodeURIComponent(origin)}`
        : baseLogoutUrl;
      const navigateWithExternal = async (url: string) => {
        if (/^https?:\/\//.test(url)) {
          await navigateTo(url, { external: true });
          return;
        }
        await navigateTo(url);
      };
      await runLogoutFlow({
        logOutSSO,
        nextTick,
        navigateTo: navigateWithExternal,
        onReset: () => {
          resetAll();
          resetStorage();
        },
        onError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[logout]', error);
        },
        redirectPath,
      });
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
